// QRCodeModule — compact "what {players|agents} see" QR card with
// inline edit.
//
// Phase 3.5: lives at the top of the Players hub's Player Requests panel
// (audience='players') and at the top of the Agents hub's Agent Requests
// panel (audience='agents').
//
// Phase 9 (2026-05-11): the audience prop now drives the BACKEND too —
// previously both audiences read/wrote the same single QR row. The
// schema now carries TWO QR/UPI sets per owner (player-facing +
// sub-agent-facing) and this component routes its reads + writes +
// uploads to the right set based on `audience`. The PATCH endpoint
// accepts both sets in one body; the upload path switches between
// /qr and /subagent-qr.

import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import WhatsAppIcon from '@baaligames/shared-ui/components/WhatsAppIcon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useDownlineLabel } from '@baaligames/shared-ui/admin/lib/downlineLabel';

type Cfg = {
  upi_id: string | null;
  qr_image_url: string | null;
  subagent_upi_id: string | null;
  subagent_qr_image_url: string | null;
  whatsapp_number: string | null;
  support_phone: string | null;
  // Phase 10 follow-up (migration 067, 2026-05-12): per-audience
  // contact channels. NULL means "fall back to the player-facing
  // pair" at read time on the backend.
  subagent_whatsapp_number: string | null;
  subagent_support_phone:   string | null;
  is_active: number;
} | null;

// Phase 9 + 10 follow-up: per-audience field-name + endpoint-suffix
// bindings. The 'players' audience reads/writes the existing columns;
// the 'agents' audience reads/writes the new subagent_* columns
// (migration 065 for upi/qr/note; migration 067 for whatsapp/phone).
// Upload endpoint suffix flips accordingly.
type AudienceFieldMap = {
  upiKey: 'upi_id' | 'subagent_upi_id';
  qrKey:  'qr_image_url' | 'subagent_qr_image_url';
  whatsappKey: 'whatsapp_number' | 'subagent_whatsapp_number';
  phoneKey:    'support_phone'   | 'subagent_support_phone';
  /** Path suffix on /admin/payment-settings used by POST + DELETE. */
  qrEndpoint: 'qr' | 'subagent-qr';
  /** Response key the upload endpoint returns the new URL under. */
  qrResponseKey: 'qr_image_url' | 'subagent_qr_image_url';
};
const FIELD_MAP_FOR: Record<'players'|'agents', AudienceFieldMap> = {
  players: {
    upiKey: 'upi_id',          qrKey: 'qr_image_url',
    whatsappKey: 'whatsapp_number', phoneKey: 'support_phone',
    qrEndpoint: 'qr',          qrResponseKey: 'qr_image_url',
  },
  agents:  {
    upiKey: 'subagent_upi_id', qrKey: 'subagent_qr_image_url',
    whatsappKey: 'subagent_whatsapp_number', phoneKey: 'subagent_support_phone',
    qrEndpoint: 'subagent-qr', qrResponseKey: 'subagent_qr_image_url',
  },
};

type Accent = 'brand' | 'sky' | 'emerald';

const ACCENT_CLS: Record<Accent, {
  primary: string;
  modalHeaderBg: string;
  modalHeaderIcon: string;
  modalHeaderBorder: string;
  focus: string;
  checkbox: string;
}> = {
  brand: {
    primary:           'bg-brand-500 hover:bg-brand-400 active:bg-brand-600 text-white shadow-[0_2px_10px_rgba(var(--brand-rgb),0.4)]',
    modalHeaderBg:     'bg-brand-50/50 dark:bg-brand-900/10',
    modalHeaderIcon:   'text-brand-600 dark:text-brand-300',
    modalHeaderBorder: 'border-brand-200/40 dark:border-brand-500/25',
    focus:             'focus:border-brand-400 focus:ring-brand-400/30',
    checkbox:          'accent-brand-500',
  },
  sky: {
    primary:           'bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)]',
    modalHeaderBg:     'bg-sky-50/50 dark:bg-sky-900/15',
    modalHeaderIcon:   'text-sky-600 dark:text-sky-300',
    modalHeaderBorder: 'border-sky-200/40 dark:border-sky-500/25',
    focus:             'focus:border-sky-400 focus:ring-sky-400/30',
    checkbox:          'accent-sky-500',
  },
  emerald: {
    primary:           'bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)]',
    modalHeaderBg:     'bg-emerald-50/60 dark:bg-emerald-900/15',
    modalHeaderIcon:   'text-emerald-600 dark:text-emerald-300',
    modalHeaderBorder: 'border-emerald-200/40 dark:border-emerald-500/25',
    focus:             'focus:border-emerald-400 focus:ring-emerald-400/30',
    checkbox:          'accent-emerald-500',
  },
};

type Props = {
  /** Whose perspective this QR card is presented from. The label
   *  copy and the highlight color flip accordingly. Backend data is
   *  the same today — there's a single `/admin/payment-settings` row
   *  per agent — but we surface the label this way now so when we
   *  later split players-QR vs agents-QR into two distinct uploads
   *  the call sites already pass the audience and don't need a
   *  second migration. */
  audience?: 'players' | 'agents';
  /** Active accent for the edit button + modal. Defaults to brand
   *  magenta; pages opt in to their own section colour (sky for
   *  Players, emerald for Agents) so the whole card stays on-theme. */
  accent?: Accent;
};

export default function QRCodeModule({ audience = 'players', accent = 'brand' }: Props) {
  const t = useT();
  const dl = useDownlineLabel();   // downline role name (Master/Agent) for audience='agents'
  const { canDo } = useAdminAuth();
  const canEdit = canDo('payment_settings', 'edit');
  const [cfg, setCfg] = useState<Cfg>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  // Phase 9: derive the audience-specific field bindings once.
  const fieldMap = useMemo(() => FIELD_MAP_FOR[audience], [audience]);

  // Audience word + its highlight color. Sky picks up the Players
  // section color; emerald picks up the Agents section color — the
  // same hues used by the bottom-nav bubble for each tab.
  //
  // Phase B Group 5 (2026-05-12): the audience WORD now comes from
  // the dictionary via two parallel keys (player vs subAgent) so
  // translators can phrase each audience independently in their
  // target language. We no longer derive it from a literal English
  // string ('players' / 'agents').
  const audienceWord = audience === 'agents'
    ? dl.lower
    : t('agent.qr.audience.player');
  const audienceCls  = audience === 'agents'
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-sky-700 dark:text-sky-300';
  const a = ACCENT_CLS[accent];

  async function load() {
    setErr(null);
    try {
      const r = await adminApi.get<{ config: Cfg }>('/admin/payment-settings');
      setCfg(r.config);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  // Phase 9: "live" means the AUDIENCE-SPECIFIC UPI is set + the row
  // is active. Each audience can have its own UPI configured; the
  // 'players' card stays Live only if upi_id is set; the 'agents'
  // card stays Live only if subagent_upi_id is set.
  const audienceUpi = cfg?.[fieldMap.upiKey] ?? null;
  const audienceQr  = cfg?.[fieldMap.qrKey]  ?? null;
  // Phase 10 follow-up (migration 067): contact chips ALSO follow the
  // audience. If this audience's variant is empty, fall back to the
  // player-facing pair so unset sub-agent variants still surface the
  // owner's player numbers (matches the backend per-field fallback).
  const audienceWhatsapp =
    (cfg && cfg[fieldMap.whatsappKey]) || cfg?.whatsapp_number || null;
  const audiencePhone =
    (cfg && cfg[fieldMap.phoneKey])   || cfg?.support_phone   || null;
  const live = !!audienceUpi && cfg?.is_active === 1;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl overflow-hidden
                   bg-white dark:bg-arena-700/40
                   border border-arena-200/60 dark:border-arena-600/40
                   shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
      >
        <div className="p-3 sm:p-4">
          {/* Side-by-side at every viewport (the hub container caps
              at max-w-xl on phones and max-w-3xl on lg+, so there's
              always room). The QR sits on the left, every other piece
              of information lives in the right column — including the
              WhatsApp + support-phone chips, so the edit button is
              the LAST element in the card and nothing looks orphaned
              below it. */}
          <div className="flex items-start gap-3">
            {/* QR thumbnail (or placeholder) with the status badge
                floating on the top-right corner of the QR itself.
                Pulling the pill onto the image frees the entire right
                column for the label + UPI + contact chips — on tiny
                phones the "what players see" label no longer gets
                truncated by the pill's footprint.

                Phase R7 follow-up (2026-05-13): wrapped the QR with
                an audience-coloured ownership badge above ("MY QR
                CODE") and an audience-coloured target line below
                ("FOR MY PLAYERS" / "FOR MY SUB-AGENTS"). Earlier the
                only signal was a small text chip in the right
                column, which agents reported as easy to miss when
                returning to the page after a while — they'd second-
                guess whether the displayed image was their own
                upload or someone else's. The bold pill + caption
                pair removes that ambiguity at a glance. */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              {/* Top ownership pill — solid audience-coloured fill
                  so it reads at a glance even on a busy page. */}
              <div className={`px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap
                               ${audience === 'agents'
                                 ? 'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white shadow-[0_1px_3px_rgba(16,185,129,0.4)]'
                                 : 'bg-sky-600 text-white dark:bg-sky-500 dark:text-white shadow-[0_1px_3px_rgba(14,165,233,0.4)]'}`}>
                My QR code
              </div>

              <div className="relative">
                {audienceQr ? (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden
                                  bg-white p-1.5 ring-1 ring-arena-200 dark:ring-arena-600/60">
                    <img
                      src={audienceQr}
                      alt={t('agent.qr.alt', { audience: audienceWord })}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl grid place-items-center
                                  bg-arena-50/60 dark:bg-arena-900/40
                                  border-2 border-dashed border-arena-300/60 dark:border-arena-600/60">
                    <Icon name="qr_code_2" size={32} className="text-arena-400 dark:text-neon-200/35" />
                  </div>
                )}
                {/* Floating status badge — top-right of the QR. */}
                <div className="absolute -top-1.5 -right-1.5 z-10">
                  <StatusPill live={live} hasCfg={!!cfg} loading={loading} />
                </div>
              </div>

              {/* Bottom target caption — audience-coloured outlined
                  pill that says explicitly who the QR is FOR. Uses
                  the muted version of the accent so it visually
                  partners with the top fill without competing with
                  it for attention. */}
              <div className={`px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap
                               border
                               ${audience === 'agents'
                                 ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-300/70 dark:border-emerald-400/40'
                                 : 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-200 border-sky-300/70 dark:border-sky-400/40'}`}>
                {audience === 'agents' ? `For my ${dl.lowerPlural}` : 'For my players'}
              </div>
            </div>

            {/* Details column — label, UPI, contact chips, edit
                button. The label row now gets the full column width
                (the status pill moved onto the QR) so on small phones
                the "what players see" copy is never truncated. */}
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <div className="inline-flex items-center gap-1.5 min-w-0">
                <Icon name="qr_code" size={12} className="text-arena-500 dark:text-neon-200/65 shrink-0" />
                {/* "My QR · what players see" / "My QR · what agents
                    see" — two parallel keys per audience so the
                    surrounding phrase reads naturally in each
                    language without us trying to splice the
                    audience word into the middle of an English
                    template. The audience-coloured span is wrapped
                    around the WHOLE label in each translation; in
                    English that visually highlights only one word
                    by accident, which we keep here for design
                    parity until translators override it. */}
                <span className={`text-[10px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70 ${audienceCls}`}>
                  {audience === 'agents' ? `My QR · what ${dl.lowerPlural} see` : t('agent.qr.cardLabel.player')}
                </span>
              </div>

              {/* UPI handle — break-all so even a long handle wraps
                  to two lines on tiny phones instead of truncating to
                  "…" and hiding the actual address. */}
              <div className="text-[14px] sm:text-[15px] font-bold text-arena-900 dark:text-white font-mono break-all leading-tight">
                {audienceUpi || (loading ? '…' : t('agent.qr.upi.empty'))}
              </div>

              {/* Fallback contact chips — INLINE inside the details
                  column above the edit button. flex-wrap means on
                  very narrow phones the second chip drops to its own
                  line; both stay visually tied to the QR card.
                  Phase 10 follow-up: chips reflect the audience —
                  agents-card shows sub-agent contacts; players-card
                  shows player contacts. Both fall back per-field to
                  the player-facing pair when unset. */}
              {(audienceWhatsapp || audiencePhone) && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {audienceWhatsapp && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]
                                     bg-emerald-50 text-emerald-800 border border-emerald-200
                                     dark:bg-emerald-400/15 dark:text-emerald-200 dark:border-emerald-400/30
                                     font-mono">
                      <WhatsAppIcon size={11} />
                      {audienceWhatsapp}
                    </span>
                  )}
                  {audiencePhone && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]
                                     bg-cyan-50 text-cyan-800 border border-cyan-200
                                     dark:bg-cyan-400/15 dark:text-cyan-200 dark:border-cyan-400/30
                                     font-mono">
                      <Icon name="call" size={11} />
                      {audiencePhone}
                    </span>
                  )}
                </div>
              )}

              {err && (
                <div className="text-[11px] text-rose-700 dark:text-rose-300">{err}</div>
              )}

              {/* Edit button — closes the card. Everything that
                  belongs to "QR & UPI" sits above it now (no orphan
                  contact row below). */}
              <div className="flex items-center gap-2 mt-1">
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                text-[12px] font-bold transition active:scale-95
                                ${a.primary}`}
                  >
                    <Icon name="edit" size={13} />
                    {audienceQr || audienceUpi ? t('agent.qr.cta.edit') : t('agent.qr.cta.setup')}
                  </button>
                )}
                {!canEdit && (
                  <span className="text-[11px] italic text-arena-500 dark:text-neon-200/55">
                    {t('agent.qr.cta.viewOnly')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {editing && (
          <QREditModal
            cfg={cfg}
            accent={accent}
            fieldMap={fieldMap}
            audience={audience}
            onClose={() => setEditing(false)}
            onSaved={(next) => { setCfg(next); setEditing(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function StatusPill({ live, hasCfg, loading }: { live: boolean; hasCfg: boolean; loading: boolean }) {
  const t = useT();
  // Solid, opaque backgrounds — same vibrancy in light AND dark mode.
  // The earlier translucent tints (bg-emerald-50 etc.) faded into the
  // white QR thumbnail in light mode and into the card surface in
  // dark mode, leaving the badge nearly invisible against either.
  // Each status gets its own colored drop-shadow that reinforces the
  // badge's hue without needing a ring border.
  const base = `shrink-0 inline-flex items-center gap-1.5 rounded-full
                px-2.5 py-1 text-[10.5px] font-extrabold tracking-wide`;

  if (loading) {
    return (
      <span className={`${base} bg-arena-500 text-white shadow-[0_2px_8px_rgba(0,0,0,0.3)]`}>
        {t('agent.qr.status.loading')}
      </span>
    );
  }
  if (live) {
    return (
      <span className={`${base} bg-emerald-500 text-white shadow-[0_3px_10px_rgba(16,185,129,0.55)]`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        {t('agent.qr.status.live')}
      </span>
    );
  }
  if (!hasCfg) {
    return (
      <span className={`${base} bg-rose-500 text-white shadow-[0_3px_10px_rgba(244,63,94,0.55)]`}>
        {t('agent.qr.status.notSet')}
      </span>
    );
  }
  // Paused — bright yellow with dark text. White text on amber has
  // borderline contrast (WCAG ~2.5:1); the dark-on-yellow pattern is
  // the standard "caution" pairing and reads cleanly in both modes.
  return (
    <span className={`${base} bg-amber-400 text-amber-950 shadow-[0_3px_10px_rgba(251,191,36,0.6)]`}>
      {t('agent.qr.status.paused')}
    </span>
  );
}

// ─── Edit modal ─────────────────────────────────────────────────────

function QREditModal({
  cfg, accent, fieldMap, audience, onClose, onSaved,
}: {
  cfg: Cfg;
  accent: Accent;
  fieldMap: AudienceFieldMap;
  audience: 'players' | 'agents';
  onClose: () => void;
  onSaved: (next: Cfg) => void;
}) {
  const t = useT();
  const a = ACCENT_CLS[accent];
  // Phase 9 + 10 follow-up: seed all editable state from the
  // AUDIENCE-SPECIFIC columns — including the whatsapp + phone pair
  // added by migration 067. is_active stays a single owner-level
  // toggle.
  const [upi, setUpi]               = useState((cfg?.[fieldMap.upiKey] ?? '') as string);
  const [whatsapp, setWhatsapp]     = useState((cfg?.[fieldMap.whatsappKey] ?? '') as string);
  const [phone, setPhone]           = useState((cfg?.[fieldMap.phoneKey]   ?? '') as string);
  const [active, setActive]         = useState(cfg?.is_active !== 0);
  const [busy, setBusy]             = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const [qrUrl, setQrUrl]           = useState((cfg?.[fieldMap.qrKey] ?? null) as string | null);
  const fileRef                     = useRef<HTMLInputElement>(null);

  // Player-facing pair (for the "Use player's contact" preview shown
  // under each input when editing the 'agents' audience). Read-only;
  // surfaced only as a hint — the actual fallback happens server-side
  // when the sub-agent variant is NULL.
  const playerWhatsapp = cfg?.whatsapp_number ?? null;
  const playerPhone    = cfg?.support_phone   ?? null;
  const isAgentsAudience = audience === 'agents';

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // Send the audience-specific UPI + contact pair under their
      // proper column names. is_active is a single owner-level toggle.
      // Empty string → null (= "fall back to the player-facing one"
      // on the agents audience, or "unset" on the players audience).
      const payload: Record<string, unknown> = { is_active: active };
      payload[fieldMap.upiKey]      = upi.trim()      || null;
      payload[fieldMap.whatsappKey] = whatsapp.trim() || null;
      payload[fieldMap.phoneKey]    = phone.trim()    || null;
      const r = await adminApi.patch<{ config: Cfg }>('/admin/payment-settings', payload);
      // Re-attach the locally-uploaded QR URL on the right field
      // because the PATCH endpoint doesn't return QR fields.
      const merged: Cfg = r.config ? { ...r.config, [fieldMap.qrKey]: qrUrl } as Cfg : r.config;
      onSaved(merged);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  async function uploadQr(rawFile: File) {
    setUploading(true); setErr(null);
    try {
      const file = await compressForUpload(rawFile, { preset: 'payment_qr' });
      const fd = new FormData();
      fd.append('qr', file);
      const r = await adminApi.postForm<Record<string, string>>(
        `/admin/payment-settings/${fieldMap.qrEndpoint}`, fd
      );
      setQrUrl(r[fieldMap.qrResponseKey] || null);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setUploading(false); }
  }

  async function removeQr() {
    if (!qrUrl) return;
    if (!confirm(t(isAgentsAudience ? 'agent.qr.modal.confirmRemove.subAgent' : 'agent.qr.modal.confirmRemove.player'))) return;
    setUploading(true); setErr(null);
    try {
      await adminApi.del(`/admin/payment-settings/${fieldMap.qrEndpoint}`);
      setQrUrl(null);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setUploading(false); }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[8000] grid place-items-start sm:place-items-center
                 p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={busy || uploading ? undefined : onClose}
      role="dialog" aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.97 }}
        transition={{ duration: 0.18 }}
        className="w-full max-w-md rounded-3xl
                   bg-white dark:bg-arena-900
                   border border-arena-200/60 dark:border-arena-600/50
                   shadow-[0_20px_60px_rgba(0,0,0,0.35)]
                   overflow-hidden my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 border-b flex items-center gap-2
                         ${a.modalHeaderBorder} ${a.modalHeaderBg}`}>
          <Icon name="qr_code_2" size={18} className={a.modalHeaderIcon} />
          <h3 className="text-[15px] font-bold text-arena-900 dark:text-white">
            {t('agent.qr.modal.title')}
          </h3>
          <button
            onClick={onClose}
            className="ml-auto grid place-items-center w-7 h-7 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-700/55 transition"
            aria-label={t('agent.qr.modal.closeAria')}
          >
            <Icon name="close" size={16} className="text-arena-600 dark:text-neon-200/80" />
          </button>
        </div>

        <form onSubmit={save} className="p-5 space-y-3">
          {/* QR preview + actions */}
          <div className="rounded-2xl p-3 flex items-center gap-3
                          bg-arena-50/60 dark:bg-arena-700/30
                          border border-arena-200/60 dark:border-arena-600/50">
            {qrUrl ? (
              <img src={qrUrl} alt="QR" className="w-20 h-20 rounded-lg object-contain bg-white p-1 ring-1 ring-arena-200 dark:ring-arena-600/50" />
            ) : (
              <div className="w-20 h-20 grid place-items-center rounded-lg
                              bg-white dark:bg-arena-900/40 ring-1 ring-arena-200 dark:ring-arena-600/50">
                <Icon name="qr_code_2" size={28} className="text-arena-400 dark:text-neon-200/35" />
              </div>
            )}
            <div className="flex-1 flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full
                           bg-cyan-50 text-cyan-700 border border-cyan-200
                           hover:bg-cyan-100 dark:bg-cyan-400/15 dark:text-cyan-200 dark:border-cyan-400/30 dark:hover:bg-cyan-400/25
                           text-[12px] font-bold transition disabled:opacity-50"
              >
                {uploading
                  ? <Icon name="progress_activity" size={13} className="animate-spin" />
                  : <Icon name={qrUrl ? 'autorenew' : 'upload'} size={13} />}
                {qrUrl ? t('agent.qr.modal.replace') : t('agent.qr.modal.upload')}
              </button>
              {qrUrl && (
                <button
                  type="button"
                  onClick={removeQr}
                  disabled={uploading}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full
                             bg-white text-rose-700 border border-rose-200 hover:bg-rose-50
                             dark:bg-arena-700/40 dark:text-rose-300 dark:border-rose-400/30 dark:hover:bg-rose-400/10
                             text-[12px] font-bold transition disabled:opacity-50"
                >
                  <Icon name="delete" size={13} /> {t('agent.qr.modal.remove')}
                </button>
              )}
            </div>
          </div>
          <input
            ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadQr(f); e.target.value = ''; }}
          />

          {/* UPI */}
          <Field label={t('agent.qr.modal.field.upi.label')}>
            <input className={inputClsFor(a)} value={upi} maxLength={120}
                   onChange={(e) => setUpi(e.target.value)}
                   placeholder={t('agent.qr.modal.field.upi.placeholder')} />
          </Field>

          {/* WhatsApp — audience-specific (migration 067). On the
              agents audience, an empty value falls back to the
              player-facing number at read time; we surface that
              fallback as a small hint so the owner knows what their
              sub-agents will see if they leave the field blank. */}
          <Field label={t(isAgentsAudience
            ? 'agent.qr.modal.field.whatsapp.label.subAgent'
            : 'agent.qr.modal.field.whatsapp.label.player')}>
            <input className={inputClsFor(a)} value={whatsapp} maxLength={40}
                   onChange={(e) => setWhatsapp(e.target.value)}
                   placeholder={isAgentsAudience && playerWhatsapp
                     ? t('agent.qr.modal.field.whatsapp.placeholder.fallback', { number: playerWhatsapp })
                     : t('agent.qr.modal.field.whatsapp.placeholder.default')} />
            {isAgentsAudience && !whatsapp.trim() && playerWhatsapp && (
              <span className="block mt-1 text-[10.5px] text-arena-500 dark:text-neon-200/55">
                {t('agent.qr.modal.field.whatsapp.fallbackHint', { number: playerWhatsapp })}
              </span>
            )}
          </Field>

          {/* Support phone — same audience-specific story. */}
          <Field label={t(isAgentsAudience
            ? 'agent.qr.modal.field.phone.label.subAgent'
            : 'agent.qr.modal.field.phone.label.player')}>
            <input className={inputClsFor(a)} value={phone} maxLength={40}
                   onChange={(e) => setPhone(e.target.value)}
                   placeholder={isAgentsAudience && playerPhone
                     ? t('agent.qr.modal.field.phone.placeholder.fallback', { number: playerPhone })
                     : t('agent.qr.modal.field.phone.placeholder.default')} />
            {isAgentsAudience && !phone.trim() && playerPhone && (
              <span className="block mt-1 text-[10.5px] text-arena-500 dark:text-neon-200/55">
                {t('agent.qr.modal.field.phone.fallbackHint', { number: playerPhone })}
              </span>
            )}
          </Field>

          {/* Active toggle */}
          <label className="flex items-center gap-3 cursor-pointer
                            rounded-xl px-3 py-2.5
                            bg-arena-50/60 dark:bg-arena-700/30
                            border border-arena-200/60 dark:border-arena-600/50">
            <input
              type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className={`w-4 h-4 ${a.checkbox}`}
            />
            <span className="flex-1 text-[12.5px] text-arena-700 dark:text-neon-200/85">
              <b className="text-arena-900 dark:text-white">{t('agent.qr.modal.active.label')}</b> · {t('agent.qr.modal.active.description')}
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
              active
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
            }`}>
              {active ? t('agent.qr.modal.active.on') : t('agent.qr.modal.active.paused')}
            </span>
          </label>

          {err && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 px-3 py-2 text-[12.5px]">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-full px-4 py-2.5 text-[13px] font-semibold transition active:scale-95 disabled:opacity-60
                         bg-arena-100/60 dark:bg-arena-600/40 hover:bg-arena-200/40 dark:hover:bg-arena-600/55
                         text-arena-700 dark:text-neon-100/85 border border-arena-200/60 dark:border-arena-600/50"
            >
              {t('agent.qr.modal.cancel')}
            </button>
            <button
              type="submit" disabled={busy}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-bold transition active:scale-95 disabled:opacity-50
                          ${a.primary}`}
            >
              {busy
                ? <Icon name="progress_activity" size={14} className="animate-spin" />
                : <Icon name="save" size={14} />}
              {t('agent.qr.modal.save')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const inputClsFor = (a: typeof ACCENT_CLS[Accent]) => `w-full rounded-xl px-3 py-2 text-[14px]
                  bg-arena-50/40 dark:bg-arena-950/40
                  border border-arena-200/60 dark:border-arena-600/50
                  text-arena-900 dark:text-white
                  placeholder-arena-400 dark:placeholder-neon-200/35
                  outline-none focus:ring-2 ${a.focus}`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
        {label}
      </span>
      {children}
    </label>
  );
}

